import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Dialog, DialogActions, DialogContent, DialogTitle
} from '@mui/material';
import { apiRequest } from 'api-client';
import apiEndpoints from 'constants/endpoints';
import { KozoomStream } from 'constants/types';
import { DynamicFormInputs } from 'pages/Common/DynamicFormInputs';
import { DynamicInput } from 'pages/Common/model';
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useAppDispatch } from 'store';
import { editDataGridRow, getDataGridList } from 'store/actions';
import { pageKeys } from 'store/reducers/dataGrid.reducer';

const getInputValue = (data: KozoomStream | undefined, name: keyof KozoomStream) => {
  return data && data[name] ? data[name] : ''
}

export const StreamAddEdit = ({ stream, eventId, open, onClose }: {stream: KozoomStream | undefined, eventId: number, open: boolean, onClose: () => void}) => {
  const [data, setData] = useState<KozoomStream>();
  const [loading, setLoading] = useState(false);
  const dispatch = useAppDispatch();
  useEffect(() => {
    if (!stream) {
      setData({
        id: 0,
        token: '',
        delay: 45000,
        server: 'https://live-kozoom.b-cdn.net/LiveApp/streams/',
        label: 'Table',
        level: 'premium',
        rank: null,
        eventId
      });
    } else {
      setData(stream);
    }
  }, [stream]);

  const inputs: DynamicInput[] = useMemo(() => {
    const results: DynamicInput[] = [
    {
      field: 'label',
      label: !data?.id ? 'Table prefix' : 'Table label',
      description: !data?.id ? 'Table prefix without number. Example: "Table"' : '',
      type: 'text',
      value: getInputValue(data, 'label'),
    },
    {
      field: 'level',
      label: 'Level',
      type: 'select',
      options: [
        { id: 'premium', label: 'Premium' },
        { id: 'club', label: 'Club' },
      ],
      value: getInputValue(data, 'level'),
    },
    {
      field: 'token',
      label: !data?.id ? 'Token prefix' : '',
      type: 'text',
      description: !data?.id ? 'Token prefix without number. Example: "kozoom"' : '',
      value: getInputValue(data, 'token')
    },
    {
      field: 'server',
      label: 'Server',
      type: 'text',
      value: getInputValue(data, 'server'),
      description: 'Leave it empty to use the legacy server'
    },
    {
      field: 'delay',
      label: 'Delay',
      type: 'number',
      value: getInputValue(data, 'delay')
    },
  ];

  if (!stream) {
    results.push({
      field: 'numOfStreams',
      label: 'Number of streams',
      type: 'number',
      value: getInputValue(data, 'numOfStreams' as keyof KozoomStream) || 1,
    });
  }
  return results;
  
}, [data, stream]);

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (data && !data.rank) {
      data.rank = null;
    }
    if (data) {
      delete data.createdAt;
      delete data.updatedAt;
      delete data.event;
    }
    if (stream) {
      dispatch(editDataGridRow(`${apiEndpoints.streams}`, stream.id, data, {}, pageKeys.eventStreams));
      onClose();
    } else {
      setLoading(true);
      apiRequest.post(`/${apiEndpoints.streams}`, data).then(data => {
        dispatch(getDataGridList(`${apiEndpoints.streams}`, {eventId }, pageKeys.eventStreams));
      }).catch(err => {
        toast.error(err.message);
      }).finally(() => {
        setLoading(false);
        onClose();
      });
    }
  }

  return (
    <Dialog onClose={onClose} maxWidth="md" fullWidth open={open}>
      <form onSubmit={onSubmit}>
        <DialogTitle>{stream ? 'Edit Stream' : 'Add Streams'}</DialogTitle>
        <DialogContent>
          <Box py={2}>
            <DynamicFormInputs inputs={inputs} onChange={(value) => setData({...data, ...value})}/>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>
            Cancel
          </Button>
          <LoadingButton loading={loading} type="submit" variant="contained" color="primary" autoFocus>
            Submit
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
}

